import React from 'react';
import { MacroState } from '../services/tradeSignalsService';

// Macro stress regime → color
export const MACRO_REGIME_COLORS: Record<string, string> = {
  calm: '#22c55e',
  cautious: '#eab308',
  stressed: '#f97316',
  extreme: '#dc2626',
  unknown: '#64748b',
};

// Plain-English regime labels — no formulas, no sigma values shown to
// subscribers. Mirrors the Notches macro panel + the ShouldITradeCard "Why?"
// expander so wording stays consistent across surfaces.
const TIER_LABEL: Record<string, string> = {
  calm:     'Calm',
  cautious: 'Slightly volatile',
  stressed: 'Volatile',
  extreme:  'Highly volatile',
  unknown:  'No data',
};

const HEADER_LABEL: Record<string, string> = {
  calm:     'Calm — no extra caution needed',
  cautious: 'Slightly volatile — minor caution',
  stressed: 'Volatile — trade smaller, respect stops',
  extreme:  'Highly volatile — consider waiting',
  unknown:  'No data',
};

// Translate the combined_penalty magnitude into a plain-English impact
// descriptor. The number itself is internal/proprietary and not shown.
function impactLabel(combined_penalty: number | null | undefined): string {
  const p = combined_penalty == null ? 0 : Number(combined_penalty);
  if (p >= 0)             return 'No impact on today’s research';
  if (p >= -0.05)         return 'Small caution on today’s research';
  if (p >= -0.15)         return 'Moderate caution on today’s research';
  return 'Heavy caution on today’s research';
}

// YYYY-MM-DD for "today" in America/New_York, regardless of browser timezone.
// Used to detect stale macro data — the brief Lambda writes once on weekdays
// around 1pm ET, so on weekends and any pre-1pm reload we're showing the last
// trading day's snapshot.
function todayInET(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year')?.value ?? '';
  const m = parts.find(p => p.type === 'month')?.value ?? '';
  const d = parts.find(p => p.type === 'day')?.value ?? '';
  return `${y}-${m}-${d}`;
}

// Render YYYY-MM-DD as "Fri Jun 12".
function formatStaleDate(iso: string): string {
  const d = new Date(iso + 'T12:00:00Z'); // noon UTC to avoid tz drift on the date itself
  return d.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

interface MacroStressPanelProps {
  macro: MacroState;
  // Retained for backwards compatibility with the two call-sites; no longer
  // affects rendered copy (we hide the per-direction penalty wording).
  penaltyTarget?: 'bullish' | 'long';
}

/**
 * Macro stress banner — single horizontal strip: combined regime headline,
 * the four oil/gold/bonds/rates factor chips with plain-English labels, and a
 * plain-English impact descriptor on the right. No sigma values, no penalty
 * numbers — those are proprietary scoring details. Shared by Trade Signals
 * and Stock Signals.
 */
export const MacroStressPanel: React.FC<MacroStressPanelProps> = ({ macro }) => {
  // Auto-detect which factor set the payload carries so the panel works for
  // BOTH the 4-factor cross-asset macro (oil/gold/bonds/rates, used by trade
  // signals + Notches) AND the equity-specific macro (vix/spy, used by
  // /stock-signals since 2026-06-27). Without this branch the equity payload
  // renders every chip as "No data" because the 4-factor keys are absent.
  const isEquity = (macro as any).vix || (macro as any).spy;
  const factors: Array<{ key: string; label: string }> = isEquity
    ? [
        { key: 'vix', label: 'Market volatility (VIX)' },
        { key: 'spy', label: 'Market trend (SPY)' },
      ]
    : [
        { key: 'oil',   label: 'Oil' },
        { key: 'gold',  label: 'Gold' },
        { key: 'bonds', label: 'Long bonds' },
        { key: 'rates', label: 'Interest rates' },
      ];
  // Default to 'unknown' on missing data (not 'calm') -- silently showing a
  // green "no extra caution needed" pill when the field is missing is the
  // worst-case failure mode. 'unknown' renders neutrally so the user knows
  // there's a data issue, not a green light.
  const combined = macro.combined_regime || 'unknown';
  const combinedColor = MACRO_REGIME_COLORS[combined] || MACRO_REGIME_COLORS.unknown;
  const headerText = HEADER_LABEL[combined] || HEADER_LABEL.unknown;
  const impact = impactLabel(macro.combined_penalty);
  const isStale = !!macro.as_of_date && macro.as_of_date !== todayInET();

  return (
    <div style={{
      marginTop: '1rem',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px 16px',
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${combinedColor}55`,
      borderRadius: 8,
      padding: '8px 14px',
    }}>
      {/* Label + combined regime headline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#e5e7eb', letterSpacing: 0.3 }}>
          MACRO BACKDROP
        </span>
        <span style={{
          fontSize: 12, fontWeight: 700,
          padding: '2px 10px', borderRadius: 4,
          backgroundColor: combinedColor, color: 'white',
        }}>{headerText}</span>
      </div>

      {/* Divider */}
      <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.12)' }} />

      {/* Four factor chips with plain-English tier labels */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 14px' }}>
        {factors.map(f => {
          const fac = (macro as any)[f.key] || { regime: 'unknown' as const };
          const color = MACRO_REGIME_COLORS[fac.regime] || MACRO_REGIME_COLORS.unknown;
          const tier = TIER_LABEL[fac.regime] || TIER_LABEL.unknown;
          return (
            <span key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, display: 'inline-block' }} />
              <span style={{ fontSize: 12, color: '#cbd5e1', fontWeight: 600 }}>{f.label}</span>
              <span style={{ fontSize: 11, color, fontWeight: 700 }}>{tier}</span>
            </span>
          );
        })}
      </div>

      {/* Impact + freshness chip — pushed to the right. The freshness chip is
          loud (amber) when stale because the read otherwise reads as current,
          and prior to this users couldn't tell that a Monday pre-1pm reload
          was showing Friday's macro. */}
      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
        {impact}
        {macro.as_of_date && (
          isStale ? (
            <span
              title={`Last update: ${formatStaleDate(macro.as_of_date)}. The macro brief refreshes around 1pm ET on trading days.`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '2px 8px', borderRadius: 4,
                background: 'rgba(234,179,8,0.15)',
                border: '1px solid rgba(234,179,8,0.45)',
                color: '#fbbf24', fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
              }}
            >
              STALE &bull; as of {formatStaleDate(macro.as_of_date)}
            </span>
          ) : (
            <span style={{ color: '#64748b' }}>&bull; Updated today</span>
          )
        )}
      </span>
    </div>
  );
};
